//External
import { Injectable } from '@angular/core';
import { Observable, catchError, map } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';

//Internal
import { Errors, POST, RequestsService } from '../../global/request-service/requests.service';
import { ConfigService } from '../../../config/config.service';
import { ValuesService } from '../../../values/values.service';
import { ProfilesService } from '../../process/profiles/profiles.service';
import { UsefulService } from '../../global/useful/useful.service';
import { SubscriptionsService } from '../../process/subscriptions/subscriptions.service';
import { ModalName, ModalRoutelessService } from '../../../components/ui/ui-modal-routeless/modal.routeless.service';
import { ModalContainerOptions, ModalContentOptions, ModalSize } from '../../global/modal/Modal.model';

@Injectable({
    providedIn: 'root'
  })
export class BucketTestingService {

    constructor(
        readonly requestsService: RequestsService,
        readonly valuesService: ValuesService,
        readonly configService: ConfigService,
        readonly cookieService: CookieService,
        readonly profileService: ProfilesService,
        readonly usefulService: UsefulService,
        readonly subscriptionsService: SubscriptionsService,
        private readonly modalRoutelessService: ModalRoutelessService
    ) { }

    /**
     * Makes the request call
     * @param {string} method The name of the method to be called
     * @param {object} params An object containing the params needed for the request
     * @returns {Observable} The response from the server
     */
    private makeRequest(method: string, params?: any): Observable<any> {
        const json = {
            id: parseInt((Math.random() * 1000).toString(), 10),
            jsonrpc: this.valuesService.jsonrpc,
            method,
            params: {}
        };

        if (params) {
            Object.assign(json.params, params);
        }

        return this.requestsService.make(
            this.configService.config.nimbusServer,
            this.valuesService.bucketTestingService,
            json,
            POST
        ).pipe(
            map(resp => {
                if (resp.error || resp.status === Errors.ERROR) {
                    throw resp.internal_data || resp;
                } else {
                    return resp?.result;
                }
            }),
            catchError(error => {
                throw error;
            })
        );
    }

    public getSettings(optionalFields?: any): Observable<any> {

        const params = {
            protocol_version: 1,
            metadata: {
                app_id: this.valuesService.appSuperapp,
                app_version: '28.0.0.1', // de unde o luam?,
                user_id: this.profileService.getCurrentContextId(),
                current_buckets: [], 

            },
            fields: {
                fingerprint: this.profileService.getOwnerFingerprint(),
                account_created_ts: this.profileService.getOwnerCreatedTs().toString(),
                country: this.profileService.getOwnerCountry(),
                os_type: "all"
            }
        };
        if (optionalFields) {
            Object.assign(params.fields, optionalFields);
        }
        return this.makeRequest('get_settings', params);
    }

    /**
     * Opens the appropriate install modal based on SuperApp eligibility.
     * - Reads the cached eligibility from sessionStorage first.
     * - If missing, fetches bucket-testing settings with mandatory fields only.
     * - When eligible, opens the SuperApp install modal with default options.
     * - Otherwise opens the classic install modal using provided options.
     * @param containerOptions Modal container options for the classic install modal
     * @param contentOptions Modal content options for the classic install modal
     * @param override Modal override options for the classic install modal
     */
    public openInstallModal(
        containerOptions?: ModalContainerOptions,
        contentOptions?: ModalContentOptions,
        override = false
    ): void {
        const openModal = (isEligible: boolean): void => {
            if (isEligible) {
                const defaultContainer: ModalContainerOptions = { size: ModalSize.LG };
                const defaultContent: any = { isFamilyPlanAdmin: this.profileService.isFamilyPlanAdmin() };
                this.modalRoutelessService.open(ModalName.installSuperappModal, defaultContainer, defaultContent, true);
                return;
            }

            const fallbackContainer = containerOptions ?? { size: ModalSize.LG };
            this.modalRoutelessService.open(ModalName.installModal, fallbackContainer, contentOptions, override);
        };

        const sessionStorageKey = this.valuesService.sessionStorageSuperappEligible;
        const cachedEligibility = sessionStorage.getItem(sessionStorageKey);
        if (cachedEligibility !== null) {
            openModal(cachedEligibility === 'true');
            return;
        }

        this.getSettings().subscribe({
            next: resp => {
                const isEligible = !!resp?.settings?.superapp;
                sessionStorage.setItem(sessionStorageKey, String(isEligible));
                openModal(isEligible);
            },
            error: error => {
                openModal(false);
            }
        });
    }

}
